/**
 * MobileBottomNav - Mobile Bottom Navigation Component
 * 
 * Provides bottom navigation for mobile users:
 * - Home
 * - Family
 * - Buy (center, prominent)
 * - Activity
 * - Profile
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Users, ShoppingBag, Clock, User } from 'lucide-react';
import { motion } from 'framer-motion';

const navItems = [
  { id: 'home', icon: Home, label: 'Home', labelEs: 'Inicio', path: '/dashboard' },
  { id: 'family', icon: Users, label: 'Family', labelEs: 'Familia', path: '/dashboard?tab=family' },
  { id: 'buy', icon: ShoppingBag, label: 'Buy', labelEs: 'Comprar', path: '/dashboard?action=buy', isPrimary: true },
  { id: 'activity', icon: Clock, label: 'Activity', labelEs: 'Actividad', path: '/dashboard?tab=activity' },
  { id: 'profile', icon: User, label: 'Profile', labelEs: 'Perfil', path: '/dashboard?tab=profile' },
];

export default function MobileBottomNav({ language = 'en', onBuyClick, activeTab = 'home' }) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = (item) => {
    if (item.id === 'buy' && onBuyClick) {
      onBuyClick();
    } else {
      navigate(item.path);
    }
  };

  // Determine active tab from URL
  const getActiveTab = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) return tab;
    if (location.pathname === '/dashboard') return 'home';
    return activeTab;
  };

  const currentTab = getActiveTab();

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 px-2 pb-safe md:hidden"
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 8px)' }}
      data-testid="mobile-bottom-nav"
    >
      <div className="flex items-center justify-around h-16">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = currentTab === item.id;
          const label = language === 'es' ? item.labelEs : item.label;

          if (item.isPrimary) {
            // Center BUY button - prominently styled
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                className="relative -mt-6"
                data-testid={`nav-${item.id}`}
              >
                <motion.div
                  whileTap={{ scale: 0.95 }}
                  className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-600 to-purple-700 shadow-lg shadow-purple-500/40 flex items-center justify-center"
                >
                  <Icon className="w-6 h-6 text-white" />
                </motion.div>
                <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-xs font-medium text-purple-600">
                  {label}
                </span>
              </button>
            );
          }

          return (
            <button
              key={item.id}
              onClick={() => handleNavClick(item)}
              className={`flex flex-col items-center justify-center min-w-[64px] h-full transition-colors ${
                isActive ? 'text-purple-600' : 'text-slate-400'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <Icon className={`w-5 h-5 ${isActive ? 'text-purple-600' : 'text-slate-400'}`} />
              <span className={`text-xs mt-1 ${isActive ? 'font-medium text-purple-600' : 'text-slate-500'}`}>
                {label}
              </span>
              {isActive && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 w-12 h-0.5 bg-purple-600 rounded-full"
                />
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
